import React from "react";

import { Schedule } from "shared/types/Schedule";

interface IProps {
  children: any;
}

export interface IClinicalEvaluationContext {
  step: number;
  setStep: (value: number) => void;
  patientObj: Schedule;
  
  // Campos do formulário de avaliação clínica
  complaint: string;
  setComplaint: (value: string) => void;
  complaintError: string;
  setComplaintError: (value: string) => void;
  
  // Evolução do tratamento
  treatmentEvolution: string;
  setTreatmentEvolution: (value: string) => void;
  treatmentEvolutionError: string;
  setTreatmentEvolutionError: (value: string) => void;
  
  // Observações clínicas
  clinicalObservations: string;
  setClinicalObservations: (value: string) => void;
  
  // Problemas identificados
  hasProblems: boolean;
  setHasProblems: (value: boolean) => void;
  problemsDescription: string;
  setProblemsDescription: (value: string) => void;
  
  // Higiene bucal
  oralHygiene: "excellent" | "good" | "regular" | "poor" | "";
  setOralHygiene: (value: "excellent" | "good" | "regular" | "poor" | "") => void;
  oralHygieneObservation: string;
  setOralHygieneObservation: (value: string) => void;
  
  // Colaboração do paciente
  patientCollaboration: "excellent" | "good" | "regular" | "poor" | "";
  setPatientCollaboration: (value: "excellent" | "good" | "regular" | "poor" | "") => void;
  
  // Recomendações
  recommendations: string;
  setRecommendations: (value: string) => void;
  
  // Função para limpar dados
  clearClinicalEvaluationData: () => void;
}

const CLINICAL_EVALUATION_STORAGE_KEY = "clinicalEvaluationFormData";

// Função para obter dados salvos do localStorage
const getSavedData = () => {
  try {
    const saved = localStorage.getItem(CLINICAL_EVALUATION_STORAGE_KEY);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (e) {
    console.error("Erro ao recuperar dados da avaliação clínica:", e);
  }
  return null;
};

const ClinicalEvaluationContext = React.createContext({} as IClinicalEvaluationContext);

const ClinicalEvaluationProvider: React.FC<IProps> = ({ children }) => {
  // Carregar dados salvos na inicialização
  const savedData = getSavedData();

  const [step, setStep] = React.useState<number>(savedData?.step || 1);
  
  const [complaint, setComplaint] = React.useState<string>(savedData?.complaint || "");
  const [complaintError, setComplaintError] = React.useState<string>("");
  
  const [treatmentEvolution, setTreatmentEvolution] = React.useState<string>(savedData?.treatmentEvolution || "");
  const [treatmentEvolutionError, setTreatmentEvolutionError] = React.useState<string>("");
  
  const [clinicalObservations, setClinicalObservations] = React.useState<string>(savedData?.clinicalObservations || "");
  
  const [hasProblems, setHasProblems] = React.useState<boolean>(savedData?.hasProblems || false);
  const [problemsDescription, setProblemsDescription] = React.useState<string>(savedData?.problemsDescription || "");
  
  const [oralHygiene, setOralHygiene] = React.useState<"excellent" | "good" | "regular" | "poor" | "">(savedData?.oralHygiene || "");
  const [oralHygieneObservation, setOralHygieneObservation] = React.useState<string>(savedData?.oralHygieneObservation || "");
  
  const [patientCollaboration, setPatientCollaboration] = React.useState<"excellent" | "good" | "regular" | "poor" | "">(savedData?.patientCollaboration || "");
  
  const [recommendations, setRecommendations] = React.useState<string>(savedData?.recommendations || "");

  const patientActually = localStorage.getItem("personAttendance");
  const patientObj = patientActually ? JSON.parse(patientActually) : null;

  // Salvar dados no localStorage sempre que houver mudança
  React.useEffect(() => {
    const dataToSave = {
      step,
      complaint,
      treatmentEvolution,
      clinicalObservations,
      hasProblems,
      problemsDescription,
      oralHygiene,
      oralHygieneObservation,
      patientCollaboration,
      recommendations,
    };
    localStorage.setItem(CLINICAL_EVALUATION_STORAGE_KEY, JSON.stringify(dataToSave));
  }, [
    step,
    complaint,
    treatmentEvolution,
    clinicalObservations,
    hasProblems,
    problemsDescription,
    oralHygiene,
    oralHygieneObservation,
    patientCollaboration,
    recommendations,
  ]);

  // Função para limpar dados da avaliação clínica
  const clearClinicalEvaluationData = React.useCallback(() => {
    localStorage.removeItem(CLINICAL_EVALUATION_STORAGE_KEY);
    setStep(1);
    setComplaint("");
    setTreatmentEvolution("");
    setClinicalObservations("");
    setHasProblems(false);
    setProblemsDescription("");
    setOralHygiene("");
    setOralHygieneObservation("");
    setPatientCollaboration("");
    setRecommendations("");
  }, []);

  const value = React.useMemo(
    () => ({
      step,
      setStep,
      patientObj,
      complaint,
      setComplaint,
      complaintError,
      setComplaintError,
      treatmentEvolution,
      setTreatmentEvolution,
      treatmentEvolutionError,
      setTreatmentEvolutionError,
      clinicalObservations,
      setClinicalObservations,
      hasProblems,
      setHasProblems,
      problemsDescription,
      setProblemsDescription,
      oralHygiene,
      setOralHygiene,
      oralHygieneObservation,
      setOralHygieneObservation,
      patientCollaboration,
      setPatientCollaboration,
      recommendations,
      setRecommendations,
      clearClinicalEvaluationData,
    }),
    [
      step,
      patientObj,
      complaint,
      complaintError,
      treatmentEvolution,
      treatmentEvolutionError,
      clinicalObservations,
      hasProblems,
      problemsDescription,
      oralHygiene,
      oralHygieneObservation,
      patientCollaboration,
      recommendations,
      clearClinicalEvaluationData,
    ]
  );

  return (
    <ClinicalEvaluationContext.Provider value={value}>
      {children}
    </ClinicalEvaluationContext.Provider>
  );
};

const useClinicalEvaluation = (): IClinicalEvaluationContext =>
  React.useContext(ClinicalEvaluationContext);

export { ClinicalEvaluationContext, ClinicalEvaluationProvider, useClinicalEvaluation };
